import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactElement,
} from "react";
import { createPortal } from "react-dom";
import { LAND_HD_MEDIA, LANDING_MOBILE_UX_MQ } from "./landingConstants";
import { preventContextMenuOnDesktop } from "./landingDomGuards";

export type CarouselSlide = { alt: string; mobile: string; desktop: string };

const DRAG_THRESHOLD_PX = 40;
const TOUCH_DRAG_THRESHOLD_PX = 28;
/** Touch swipe uses TouchEvents below this width; pointer path ignores touch to avoid double moves. */
const MOBILE_CAROUSEL_MQ = LANDING_MOBILE_UX_MQ;

/** Double-tap expand: max ms between taps, max px between release points. */
const DOUBLE_TAP_MS = 360;
const DOUBLE_TAP_MAX_DIST_PX = 72;

export type CarouselDragGateEvent = PointerEvent | TouchEvent;

/** Fraction of the carousel’s area that must intersect the viewport before autoplay runs. */
const CAROUSEL_AUTOPLAY_MIN_INTERSECTION = 0.5;

/**
 * True when at least half of the carousel root is visible in the viewport and the tab is visible.
 * Not used by the inner faction hero Spine carousel.
 */
export function useCarouselAutoplayInView(
  wrapRef: React.RefObject<HTMLElement | null>
): boolean {
  const [allowed, setAllowed] = useState(false);
  const intersectingRef = useRef(false);

  useEffect(() => {
    const el = wrapRef.current;
    if (!el) return;

    const sync = (): void => {
      const tabVisible =
        typeof document !== "undefined" &&
        document.visibilityState === "visible" &&
        !document.hidden;
      setAllowed(intersectingRef.current && tabVisible);
    };

    const io = new IntersectionObserver(
      (entries) => {
        const e = entries.find((ent) => ent.target === el) ?? entries[0];
        const ratio = e?.intersectionRatio ?? 0;
        intersectingRef.current =
          e?.isIntersecting === true && ratio >= CAROUSEL_AUTOPLAY_MIN_INTERSECTION;
        sync();
      },
      {
        root: null,
        rootMargin: "0px",
        threshold: [0, 0.25, 0.5, 0.75, 1],
      }
    );
    io.observe(el);
    document.addEventListener("visibilitychange", sync);

    return () => {
      io.disconnect();
      document.removeEventListener("visibilitychange", sync);
    };
  }, [wrapRef]);

  return allowed;
}

function attachPointerOnlyDrag(
  el: HTMLDivElement,
  move: (dir: number) => void,
  canBegin: ((e: CarouselDragGateEvent) => boolean) | undefined
): () => void {
  const drag = { x: 0, pointerId: -1, active: false };

  const onPointerDown = (e: PointerEvent) => {
    /* Touch is handled by `attachTouchSwipeDrag` (narrow or coarse pointer). Capturing touch here
       blocks vertical page scroll (e.g. phone landscape width > 899px). */
    if (e.pointerType === "touch") return;
    if (e.pointerType === "mouse" && e.button !== 0) return;
    if ((e.target as HTMLElement).closest("button")) return;
    if (canBegin && !canBegin(e)) return;
    drag.x = e.clientX;
    drag.pointerId = e.pointerId;
    drag.active = true;
    try {
      el.setPointerCapture(e.pointerId);
    } catch {
      /* already captured */
    }
  };

  const finish = (e: PointerEvent, applyMove: boolean) => {
    if (!drag.active || e.pointerId !== drag.pointerId) return;
    drag.active = false;
    try {
      el.releasePointerCapture(e.pointerId);
    } catch {
      /* */
    }
    if (applyMove) {
      const dx = e.clientX - drag.x;
      if (Math.abs(dx) > DRAG_THRESHOLD_PX) move(dx < 0 ? 1 : -1);
    }
    drag.pointerId = -1;
  };

  const onPointerUp = (e: PointerEvent) => {
    finish(e, true);
  };

  const onPointerCancel = (e: PointerEvent) => {
    finish(e, false);
  };

  el.addEventListener("pointerdown", onPointerDown);
  el.addEventListener("pointerup", onPointerUp);
  el.addEventListener("pointercancel", onPointerCancel);
  return () => {
    el.removeEventListener("pointerdown", onPointerDown);
    el.removeEventListener("pointerup", onPointerUp);
    el.removeEventListener("pointercancel", onPointerCancel);
  };
}

function attachTouchSwipeDrag(
  el: HTMLDivElement,
  move: (dir: number) => void,
  canBegin: ((e: CarouselDragGateEvent) => boolean) | undefined
): () => void {
  let startX = 0;
  let startY = 0;
  let tracking = false;
  let touchId = -1;

  const reset = () => {
    tracking = false;
    touchId = -1;
  };

  const onTouchStart = (e: TouchEvent) => {
    if (e.touches.length !== 1) {
      reset();
      return;
    }
    if ((e.target as HTMLElement).closest("button")) return;
    if (canBegin && !canBegin(e)) return;
    const t = e.touches[0];
    startX = t.clientX;
    startY = t.clientY;
    touchId = t.identifier;
    tracking = true;
  };

  const onTouchEnd = (e: TouchEvent) => {
    if (!tracking || touchId < 0) return;
    const t = Array.from(e.changedTouches).find((x) => x.identifier === touchId);
    reset();
    if (!t) return;
    const dx = t.clientX - startX;
    const dy = t.clientY - startY;
    /* Horizontal swipe: dominant dx, but not so strict that small vertical drift kills the gesture. */
    if (Math.abs(dx) > TOUCH_DRAG_THRESHOLD_PX && Math.abs(dx) > Math.abs(dy) + 4) {
      move(dx < 0 ? 1 : -1);
    }
  };

  const onTouchCancel = () => {
    reset();
  };

  const endOpts: AddEventListenerOptions = { capture: true, passive: true };
  el.addEventListener("touchstart", onTouchStart, { passive: true });
  el.addEventListener("touchend", onTouchEnd, endOpts);
  el.addEventListener("touchcancel", onTouchCancel, { capture: true });
  return () => {
    el.removeEventListener("touchstart", onTouchStart);
    el.removeEventListener("touchend", onTouchEnd, endOpts);
    el.removeEventListener("touchcancel", onTouchCancel, { capture: true });
  };
}

/** True when we attach touch swipe listeners (not only narrow width — e.g. phone landscape). */
const CAROUSEL_TOUCH_DRAG_MQ = "(pointer: coarse)";

function mqAddChange(mq: MediaQueryList, fn: () => void): void {
  if (typeof mq.addEventListener === "function") mq.addEventListener("change", fn);
  else mq.addListener(fn);
}

function mqRemoveChange(mq: MediaQueryList, fn: () => void): void {
  if (typeof mq.removeEventListener === "function") mq.removeEventListener("change", fn);
  else mq.removeListener(fn);
}

/**
 * When double-tap expand is enabled: narrow layout OR coarse pointer (e.g. phone in landscape
 * where width exceeds `LANDING_MOBILE_UX_MQ`). Matches when `useCarouselPointerDrag` attaches
 * touch swipe.
 */
function useExpandDoubleTapTouchUxMq(): boolean {
  const [matches, setMatches] = useState(false);
  useEffect(() => {
    if (typeof window === "undefined") return;
    const mqNarrow = window.matchMedia(LANDING_MOBILE_UX_MQ);
    const mqCoarse = window.matchMedia(CAROUSEL_TOUCH_DRAG_MQ);
    const sync = (): void => setMatches(mqNarrow.matches || mqCoarse.matches);
    sync();
    mqAddChange(mqNarrow, sync);
    mqAddChange(mqCoarse, sync);
    return () => {
      mqRemoveChange(mqNarrow, sync);
      mqRemoveChange(mqCoarse, sync);
    };
  }, []);
  return matches;
}

export function useCarouselPointerDrag(
  wrapRef: React.RefObject<HTMLDivElement | null>,
  move: (dir: number) => void,
  /**
   * Return false to ignore this gesture (no capture / tracking). Use so an outer carousel
   * does not steal pointer capture from an inner one (e.g. faction strip vs hero Spine).
   */
  canBegin?: (e: CarouselDragGateEvent) => boolean,
  /** When false, listeners are not attached (e.g. main strip while mobile lightbox is open). */
  active = true,
  /** Bump when the wrap node mounts late so the effect re-binds (e.g. portal callback ref). */
  rebindKey = 0
): void {
  useEffect(() => {
    if (typeof window === "undefined") return;
    if (!active) {
      return () => undefined;
    }
    const el = wrapRef.current;
    if (!el) {
      return () => undefined;
    }

    const mqNarrow = window.matchMedia(MOBILE_CAROUSEL_MQ);
    const mqCoarse = window.matchMedia(CAROUSEL_TOUCH_DRAG_MQ);
    const touchDragActive = (): boolean => mqNarrow.matches || mqCoarse.matches;

    const noopDetach = (): void => {
      return;
    };
    let detachPointer = noopDetach;
    let detachTouch = noopDetach;

    const bind = (): void => {
      detachPointer();
      detachTouch();
      detachPointer = attachPointerOnlyDrag(el, move, canBegin);
      detachTouch = touchDragActive() ? attachTouchSwipeDrag(el, move, canBegin) : noopDetach;
    };

    bind();
    mqAddChange(mqNarrow, bind);
    mqAddChange(mqCoarse, bind);
    return () => {
      mqRemoveChange(mqNarrow, bind);
      mqRemoveChange(mqCoarse, bind);
      detachPointer();
      detachTouch();
    };
  }, [wrapRef, move, canBegin, active, rebindKey]);
}

export type ImageCarouselProps = {
  slides: CarouselSlide[];
  intervalMs: number;
  ariaLabel: string;
  /** Extra classes on the outer wrap (e.g. `carousel-wrap-inner`). */
  wrapClassName?: string;
  /**
   * When true on touch-style viewports (narrow per `LANDING_MOBILE_UX_MQ`, or coarse pointer
   * e.g. phone landscape), double-tap the viewport opens a full-screen framed preview (HD asset);
   * double-tap backdrop again or Close / Escape dismisses.
   */
  expandOnDoubleTapMobile?: boolean;
  /** Desktop (≥900px): icon control on the strip toggles parent section width (Art / Gameplay). */
  desktopStripExpand?: {
    expanded: boolean;
    onToggle: () => void;
  };
};

let galleryStripIconMarkerSeq = 0;
function nextGalleryStripMarkerId(prefix: string): string {
  galleryStripIconMarkerSeq += 1;
  return `gse-${prefix}-${galleryStripIconMarkerSeq}`;
}

function IconStripExpand(): ReactElement {
  const mk = nextGalleryStripMarkerId("out");
  return (
    <svg
      className="gallery-strip-expand-svg"
      viewBox="0 0 24 24"
      width={22}
      height={22}
      aria-hidden
    >
      <defs>
        <marker
          id={mk}
          markerWidth="7"
          markerHeight="7"
          refX="6"
          refY="3.5"
          orient="auto"
          markerUnits="userSpaceOnUse"
        >
          <path d="M0 0 L6.5 3.5 L0 7 Z" fill="currentColor" />
        </marker>
      </defs>
      <g fill="none" stroke="currentColor" strokeWidth="1.65" strokeLinecap="round" strokeLinejoin="round">
        <line x1="11.5" y1="11.5" x2="4.2" y2="4.2" markerEnd={`url(#${mk})`} />
        <line x1="12.5" y1="11.5" x2="19.8" y2="4.2" markerEnd={`url(#${mk})`} />
        <line x1="11.5" y1="12.5" x2="4.2" y2="19.8" markerEnd={`url(#${mk})`} />
        <line x1="12.5" y1="12.5" x2="19.8" y2="19.8" markerEnd={`url(#${mk})`} />
      </g>
    </svg>
  );
}

function IconStripCollapse(): ReactElement {
  const mk = nextGalleryStripMarkerId("in");
  return (
    <svg
      className="gallery-strip-expand-svg"
      viewBox="0 0 24 24"
      width={22}
      height={22}
      aria-hidden
    >
      <defs>
        <marker
          id={mk}
          markerWidth="7"
          markerHeight="7"
          refX="6"
          refY="3.5"
          orient="auto"
          markerUnits="userSpaceOnUse"
        >
          <path d="M0 0 L6.5 3.5 L0 7 Z" fill="currentColor" />
        </marker>
      </defs>
      <g fill="none" stroke="currentColor" strokeWidth="1.65" strokeLinecap="round" strokeLinejoin="round">
        <line x1="4.2" y1="4.2" x2="11.5" y2="11.5" markerEnd={`url(#${mk})`} />
        <line x1="19.8" y1="4.2" x2="12.5" y2="11.5" markerEnd={`url(#${mk})`} />
        <line x1="4.2" y1="19.8" x2="11.5" y2="12.5" markerEnd={`url(#${mk})`} />
        <line x1="19.8" y1="19.8" x2="12.5" y2="12.5" markerEnd={`url(#${mk})`} />
      </g>
    </svg>
  );
}

type TrackEntry = {
  slide: CarouselSlide;
  reactKey: string;
  /** Real slide index for alt / eager loading (clone edges map to their source). */
  logicalIndex: number;
};

function buildTrackEntries(slides: CarouselSlide[]): TrackEntry[] {
  const n = slides.length;
  if (n <= 1) {
    return slides.map((slide, i) => ({
      slide,
      reactKey: `slide-${i}`,
      logicalIndex: i,
    }));
  }
  const last = slides[n - 1];
  const first = slides[0];
  return [
    { slide: last, reactKey: "edge-clone-last", logicalIndex: n - 1 },
    ...slides.map((slide, i) => ({
      slide,
      reactKey: `slide-${i}`,
      logicalIndex: i,
    })),
    { slide: first, reactKey: "edge-clone-first", logicalIndex: 0 },
  ];
}

function logicalIndexFromPosition(position: number, total: number): number {
  if (total <= 1) return 0;
  if (position === 0) return total - 1;
  if (position === total + 1) return 0;
  return position - 1;
}

/**
 * Image carousel (picture + HD source). Autoplay, dots, prev/next, and
 * pointer-drag (desktop + touch pointers) to change slides.
 */
const ImageCarousel = ({
  slides,
  intervalMs,
  ariaLabel,
  wrapClassName = "",
  expandOnDoubleTapMobile = false,
  desktopStripExpand,
}: ImageCarouselProps): ReactElement => {
  const total = slides.length;
  const trackEntries = useMemo(() => buildTrackEntries(slides), [slides]);
  const trackLen = trackEntries.length;
  const expandDoubleTapTouchUx = useExpandDoubleTapTouchUxMq();

  const [position, setPosition] = useState(() => (total <= 1 ? 0 : 1));
  const [noTransition, setNoTransition] = useState(false);
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const wrapRef = useRef<HTMLDivElement | null>(null);
  const trackRef = useRef<HTMLDivElement | null>(null);
  const lightboxWrapRef = useRef<HTMLDivElement | null>(null);
  const lightboxTrackRef = useRef<HTMLDivElement | null>(null);
  const [lightboxDragRebind, setLightboxDragRebind] = useState(0);
  const lightboxCloseRef = useRef<HTMLButtonElement | null>(null);
  const doubleTapRef = useRef<{ t: number; x: number; y: number }>({ t: 0, x: 0, y: 0 });
  const positionRef = useRef(position);
  positionRef.current = position;

  useEffect(() => {
    setPosition(total <= 1 ? 0 : 1);
  }, [total]);

  const jumpWithoutTransition = useCallback((next: number) => {
    setNoTransition(true);
    setPosition(next);
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        setNoTransition(false);
      });
    });
  }, []);

  const handleTrackTransitionEnd = useCallback(
    (e: React.TransitionEvent<HTMLDivElement>) => {
      if (e.propertyName !== "transform") return;
      if (lightboxOpen) {
        if (e.target !== lightboxTrackRef.current) return;
      } else if (e.target !== trackRef.current) {
        return;
      }
      if (total <= 1) return;
      const pos = positionRef.current;
      if (pos === total + 1) {
        jumpWithoutTransition(1);
      } else if (pos === 0) {
        jumpWithoutTransition(total);
      }
    },
    [jumpWithoutTransition, lightboxOpen, total]
  );

  const move = useCallback(
    (dir: number) => {
      if (total <= 1) return;
      setPosition((p) => {
        if (dir > 0) {
          if (p >= total + 1) return total + 1;
          return p + 1;
        }
        if (p <= 0) return 0;
        if (p === 1) return 0;
        return p - 1;
      });
    },
    [total]
  );

  const goTo = useCallback(
    (i: number) => {
      if (total <= 1) {
        setPosition(0);
        return;
      }
      const clamped = Math.max(0, Math.min(i, total - 1));
      setPosition(clamped + 1);
    },
    [total]
  );

  const logicalIndex = logicalIndexFromPosition(position, total);

  const [autoplayResetKey, setAutoplayResetKey] = useState(0);
  const bumpAutoplay = useCallback(() => {
    setAutoplayResetKey((k) => k + 1);
  }, []);

  const moveFromUser = useCallback(
    (dir: number) => {
      bumpAutoplay();
      move(dir);
    },
    [bumpAutoplay, move]
  );

  const goToFromUser = useCallback(
    (i: number) => {
      bumpAutoplay();
      goTo(i);
    },
    [bumpAutoplay, goTo]
  );

  const autoplayAllowed = useCarouselAutoplayInView(wrapRef);

  useEffect(() => {
    if (!autoplayAllowed || lightboxOpen) return;
    const t = window.setInterval(() => move(1), intervalMs);
    return () => window.clearInterval(t);
  }, [autoplayAllowed, autoplayResetKey, intervalMs, move, lightboxOpen]);

  useEffect(() => {
    if (!lightboxOpen) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prev;
    };
  }, [lightboxOpen]);

  useEffect(() => {
    if (!lightboxOpen) return;
    const onKey = (e: KeyboardEvent): void => {
      if (e.key === "Escape") setLightboxOpen(false);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [lightboxOpen]);

  useEffect(() => {
    if (lightboxOpen) lightboxCloseRef.current?.focus();
  }, [lightboxOpen]);

  useEffect(() => {
    doubleTapRef.current = { t: 0, x: 0, y: 0 };
  }, [lightboxOpen]);

  const consumeDoubleTap = useCallback(
    (e: React.TouchEvent, then: () => void): void => {
      if (e.changedTouches.length !== 1) return;
      const touch = e.changedTouches[0];
      if ((e.target as HTMLElement).closest("button")) return;
      const now = Date.now();
      const prev = doubleTapRef.current;
      const dist = Math.hypot(touch.clientX - prev.x, touch.clientY - prev.y);
      const dt = prev.t > 0 ? now - prev.t : 99999;
      if (dt < DOUBLE_TAP_MS && dist < DOUBLE_TAP_MAX_DIST_PX) {
        e.preventDefault();
        then();
        doubleTapRef.current = { t: 0, x: 0, y: 0 };
      } else {
        doubleTapRef.current = { t: now, x: touch.clientX, y: touch.clientY };
      }
    },
    []
  );

  const onViewportTouchEnd = useCallback(
    (e: React.TouchEvent<HTMLDivElement>) => {
      if (!expandOnDoubleTapMobile || !expandDoubleTapTouchUx || lightboxOpen) return;
      consumeDoubleTap(e, () => setLightboxOpen(true));
    },
    [consumeDoubleTap, expandDoubleTapTouchUx, expandOnDoubleTapMobile, lightboxOpen]
  );

  const onLightboxDoubleTapCloseTouchEnd = useCallback(
    (e: React.TouchEvent<HTMLDivElement>) => {
      if (!lightboxOpen) return;
      consumeDoubleTap(e, () => setLightboxOpen(false));
    },
    [consumeDoubleTap, lightboxOpen]
  );

  const setLightboxWrapRef = useCallback((node: HTMLDivElement | null) => {
    lightboxWrapRef.current = node;
    if (node) setLightboxDragRebind((k) => k + 1);
  }, []);

  useCarouselPointerDrag(wrapRef, moveFromUser, undefined, !lightboxOpen);
  const lightboxPointerDragActive =
    expandOnDoubleTapMobile && expandDoubleTapTouchUx && lightboxOpen && total > 1;
  useCarouselPointerDrag(
    lightboxWrapRef,
    moveFromUser,
    undefined,
    lightboxPointerDragActive,
    lightboxDragRebind
  );

  const wrapClass = `carousel-wrap carousel-wrap--image-strip carousel-wrap-grabbable${wrapClassName ? ` ${wrapClassName}` : ""}`;
  const trackClass = `carousel-track${noTransition ? " carousel-track--no-trans" : ""}`;
  const transform =
    trackLen > 0
      ? `translateX(calc((-1 * ${position} * 100%) / ${trackLen}))`
      : "translateX(0)";

  const expandStripClass = `carousel-wrap carousel-wrap--image-strip carousel-wrap-grabbable image-carousel-expand-strip${wrapClassName ? ` ${wrapClassName}` : ""}`;

  const lightboxPortal =
    expandOnDoubleTapMobile &&
    lightboxOpen &&
    slides.length > 0 &&
    typeof document !== "undefined"
      ? createPortal(
          <div
            className="land-home image-carousel-expand-overlay-root"
            role="dialog"
            aria-modal="true"
            aria-label={`${ariaLabel} — expanded view`}
          >
            <div
              className="image-carousel-expand-backdrop"
              aria-hidden
              onTouchEnd={onLightboxDoubleTapCloseTouchEnd}
            />
            <div className="image-carousel-expand-content">
              <div
                className="image-carousel-expand-stage"
                onTouchEnd={onLightboxDoubleTapCloseTouchEnd}
              >
                <div className="carousel-section image-carousel-expand-section">
                  <div ref={setLightboxWrapRef} className={expandStripClass}>
                    <div
                      className="carousel-viewport"
                      style={
                        {
                          ["--carousel-len" as string]: String(trackLen),
                        } as React.CSSProperties
                      }
                    >
                      <div
                        ref={lightboxTrackRef}
                        className={trackClass}
                        style={{ transform }}
                        onTransitionEnd={handleTrackTransitionEnd}
                      >
                        {trackEntries.map((entry) => (
                          <div key={entry.reactKey} className="carousel-slide">
                            <div className="image-carousel-expand-slide-mat">
                              <picture
                                onContextMenu={preventContextMenuOnDesktop}
                              >
                                <source media={LAND_HD_MEDIA} srcSet={entry.slide.desktop} />
                                <img
                                  src={entry.slide.desktop}
                                  alt={entry.slide.alt}
                                  loading={entry.logicalIndex === logicalIndex ? "eager" : "lazy"}
                                  decoding="async"
                                  draggable={false}
                                  onDragStart={(ev) => ev.preventDefault()}
                                  onContextMenu={preventContextMenuOnDesktop}
                                />
                              </picture>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                    <button
                      type="button"
                      className="carousel-btn prev"
                      onClick={() => moveFromUser(-1)}
                      aria-label="Previous slide"
                    >
                      &#8249;
                    </button>
                    <button
                      type="button"
                      className="carousel-btn next"
                      onClick={() => moveFromUser(1)}
                      aria-label="Next slide"
                    >
                      &#8250;
                    </button>
                  </div>
                  <div className="carousel-dots image-carousel-expand-dots">
                    {slides.map((_, i) => (
                      <button
                        key={i}
                        type="button"
                        className={`carousel-dot${i === logicalIndex ? " active" : ""}`}
                        onClick={() => goToFromUser(i)}
                        aria-label={`Go to slide ${i + 1}`}
                        aria-current={i === logicalIndex}
                      />
                    ))}
                  </div>
                </div>
              </div>
            </div>
            <button
              ref={lightboxCloseRef}
              type="button"
              className="image-carousel-expand-close"
              aria-label="Close expanded image"
              onClick={() => setLightboxOpen(false)}
            >
              ×
            </button>
          </div>,
          document.body
        )
      : null;

  return (
    <>
      <div className={wrapClass} ref={wrapRef} role="region" aria-label={ariaLabel}>
        <div
          className="carousel-viewport"
          onTouchEnd={
            expandOnDoubleTapMobile && expandDoubleTapTouchUx ? onViewportTouchEnd : undefined
          }
          style={
            {
              ["--carousel-len" as string]: String(trackLen),
            } as React.CSSProperties
          }
        >
          <div
            ref={trackRef}
            className={trackClass}
            style={{ transform }}
            onTransitionEnd={handleTrackTransitionEnd}
          >
            {trackEntries.map((entry) => (
              <div key={entry.reactKey} className="carousel-slide">
                <picture onContextMenu={preventContextMenuOnDesktop}>
                  <source media={LAND_HD_MEDIA} srcSet={entry.slide.desktop} />
                  <img
                    src={entry.slide.mobile}
                    alt={entry.slide.alt}
                    loading={entry.logicalIndex === 0 ? "eager" : "lazy"}
                    decoding="async"
                    draggable={false}
                    onDragStart={(e) => e.preventDefault()}
                    onContextMenu={preventContextMenuOnDesktop}
                  />
                </picture>
              </div>
            ))}
          </div>
        </div>
        {desktopStripExpand ? (
          <button
            type="button"
            className="gallery-strip-expand-btn"
            aria-pressed={desktopStripExpand.expanded}
            aria-label={
              desktopStripExpand.expanded
                ? "Collapse gallery width"
                : "Expand gallery width"
            }
            onClick={(e) => {
              e.stopPropagation();
              desktopStripExpand.onToggle();
              if (e.detail > 0) {
                (e.currentTarget as HTMLButtonElement).blur();
              }
            }}
          >
            {desktopStripExpand.expanded ? <IconStripCollapse /> : <IconStripExpand />}
          </button>
        ) : null}
        <button
          type="button"
          className="carousel-btn prev"
          onClick={() => moveFromUser(-1)}
          aria-label="Previous slide"
        >
          &#8249;
        </button>
        <button
          type="button"
          className="carousel-btn next"
          onClick={() => moveFromUser(1)}
          aria-label="Next slide"
        >
          &#8250;
        </button>
      </div>
      <div className="carousel-dots">
        {slides.map((_, i) => (
          <button
            key={i}
            type="button"
            className={`carousel-dot${i === logicalIndex ? " active" : ""}`}
            onClick={() => goToFromUser(i)}
            aria-label={`Go to slide ${i + 1}`}
            aria-current={i === logicalIndex}
          />
        ))}
      </div>
      {lightboxPortal}
    </>
  );
};

export default ImageCarousel;
